import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  FiberManualRecord as ModifiedIcon,
  SaveAs as SaveAsIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import type { 
  Template as ChartTemplate, 
  TemplateAccessLevel
} from '../../services/templates/schemas/templateSchema';
import type { ActiveTemplateState } from '../../interfaces';

export interface TemplateDropdownProps {
  // Template data
  templates: ChartTemplate[];
  activeTemplate: ActiveTemplateState;
  loading?: boolean;
  
  // Current user context
  currentUserId: string;
  isErdosMiller?: boolean; // Only Erdos Miller can set templates to public
  
  // Callbacks
  onTemplateChange: (templateId: string | undefined) => void;
  onSaveTemplate: () => void;
  onSaveAsNewTemplate: (newName: string) => void;
  onDeleteTemplate: (templateId: string) => void;
  
  // UI props
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const TemplateDropdown: React.FC<TemplateDropdownProps> = ({
  templates,
  activeTemplate,
  loading = false,
  currentUserId,
  isErdosMiller: _isErdosMiller = false,
  onTemplateChange,
  onSaveTemplate,
  onSaveAsNewTemplate,
  onDeleteTemplate,
  disabled = false,
  size = 'small'
}) => {
  const [saveAsDialogOpen, setSaveAsDialogOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<ChartTemplate | null>(null);

  const getAccessLevelLabel = (accessLevel: TemplateAccessLevel) => {
    switch (accessLevel) {
      case 'public': return 'Public';
      case 'org': return 'Team';
      case 'private': return 'Private';
    }
  };

  const getAccessLevelColor = (accessLevel: TemplateAccessLevel): 'success' | 'primary' | 'default' => {
    switch (accessLevel) {
      case 'public': return 'success';
      case 'org': return 'primary';
      case 'private': return 'default';
    }
  };

  // Find current template for save functionality
  const currentTemplate = templates.find(t => t.id === activeTemplate.activeTemplateId);
  const canSave = currentTemplate && currentTemplate.userId === currentUserId;
  const hasChanges = activeTemplate.hasUnsavedChanges;



  const handleSaveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onSaveTemplate();
  };

  const handleSaveAsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    // Don't pre-populate - let user start typing immediately
    setNewTemplateName('');
    setSaveAsDialogOpen(true);
  };

  const handleSaveAsConfirm = () => {
    // Use placeholder text as fallback if user didn't type anything
    const finalName = newTemplateName.trim() || `${currentTemplate?.name || 'Untitled Template'} Copy`;
    onSaveAsNewTemplate(finalName);
    setSaveAsDialogOpen(false);
    setNewTemplateName('');
  };

  const handleSaveAsCancel = () => {
    setSaveAsDialogOpen(false);
    setNewTemplateName('');
  };

  const handleDeleteClick = (event: React.MouseEvent, template: ChartTemplate) => {
    event.stopPropagation();
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      onDeleteTemplate(templateToDelete.id);
      setDeleteConfirmOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const handleTemplateSelect = (value: string) => {
    // Handle special save action values
    if (value === '__save__') {
      handleSaveClick({ stopPropagation: () => {} } as React.MouseEvent);
      return;
    }
    if (value === '__save_as__') {
      handleSaveAsClick({ stopPropagation: () => {} } as React.MouseEvent);
      return;
    }
    
    // Handle normal template selection
    onTemplateChange(value || undefined);
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Template Label with Change Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 'fit-content' }}>
          <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary' }}>
            Template
          </Typography>
          {hasChanges && (
            <Tooltip title="Template has unsaved changes">
              <ModifiedIcon sx={{ fontSize: 14, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
        
        {/* Template Selection Dropdown */}
        <FormControl 
          size={size} 
          sx={{ 
            minWidth: 240,
            flex: 1,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderWidth: 2
              },
              '&:hover fieldset': {
                borderWidth: 2
              },
              '&.Mui-focused fieldset': {
                borderWidth: 2
              }
            }
          }}
        >
          <Select
            value={activeTemplate.activeTemplateId || ''}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            disabled={disabled || loading}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return (
                  <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No template
                  </Typography>
                );
              }
              const template = templates.find(t => t.id === selected);
              if (template) {
                return template.name;
              }
              
              // Template not found in current array - likely during async update
              // Try to get name from activeTemplate state as fallback
              if (activeTemplate.originalTemplateData?.id === selected) {
                return activeTemplate.originalTemplateData.name;
              }
              
              // Last resort - show loading instead of "Unknown" to prevent onChange
              return (
                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Loading...
                </Typography>
              );
            }}
            sx={{
              '& .MuiSelect-select': {
                py: size === 'small' ? 1 : 1.5
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: { 
                  maxHeight: 400,
                  '& .MuiMenu-list': {
                    padding: 0, // Remove default padding so sticky positioning works correctly
                  }
                }
              }
            }}
          >
            {/* Sticky Save Options */}
            {hasChanges && canSave && (
              <MenuItem 
                value="__save__"
                sx={{ 
                  position: 'sticky',
                  top: 0,
                  backgroundColor: 'background.paper',
                  zIndex: 10,
                  color: 'primary.main', 
                  fontWeight: 600,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                <ListItemIcon>
                  <SaveIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Save Changes"
                  secondary={`Update "${currentTemplate?.name}"`}
                />
              </MenuItem>
            )}
            
            {(hasChanges || !activeTemplate.activeTemplateId) && (
              <MenuItem 
                value="__save_as__"
                sx={{ 
                  position: 'sticky',
                  top: hasChanges && canSave ? '56px' : 0, // Position below save if it exists
                  backgroundColor: 'background.paper',
                  zIndex: 10,
                  color: 'primary.main', 
                  fontWeight: 600,
                  borderBottom: '1px solid',
                  borderBottomColor: 'divider',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText'
                  }
                }}
              >
                <ListItemIcon>
                  <SaveAsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Save as New Template"
                  secondary={
                    activeTemplate.activeTemplateId 
                      ? "Create new from current state" 
                      : "Save current styling as template"
                  }
                />
              </MenuItem>
            )}

            {/* No Template Option */}
            <MenuItem value="">
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No template
              </Typography>
            </MenuItem>
            
            {/* Template List */}
            {templates.map((template) => {
              const canDelete = template.userId === currentUserId;
              
              return (
                <MenuItem key={template.id} value={template.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                      {template.name}
                    </Typography>
                    
                    <Chip
                      label={getAccessLevelLabel(template.accessLevel)}
                      size="small"
                      color={getAccessLevelColor(template.accessLevel)}
                      variant="outlined"
                      sx={{ 
                        fontSize: '0.75rem',
                        height: 20,
                        minWidth: 50
                      }}
                    />
                    
                    {/* Delete Button - Only for owned templates */}
                    {canDelete && (
                      <Tooltip title="Delete template">
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, template)}
                          sx={{ 
                            ml: 0.5,
                            opacity: 0.6,
                            '&:hover': { 
                              opacity: 1,
                              color: 'error.main' 
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Stack>

      {/* Save As Dialog */}
      <Dialog open={saveAsDialogOpen} onClose={handleSaveAsCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Save as New Template</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Template Name"
            fullWidth
            variant="outlined"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            placeholder={`${currentTemplate?.name || 'Untitled Template'} Copy`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveAsConfirm();
              }
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveAsCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAsConfirm} 
            variant="contained" 
            startIcon={<SaveAsIcon />}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TemplateDropdown; 